import { useMsalToken } from "@/hooks/use-msal-token";
import { API_ENDPOINTS, API_TAGS, TAG_BUILDER, createTag } from "./abstractions/api-types";
import { useAuthenticatedApi } from "./abstractions/use-authenticated-api";
import type {
  AdminStatsResponse,
  User
} from "@/domain/core";
import {
  buildHttpGet,
  buildHttpPost,
  buildHttpDelete
} from "@/utilities/api-utils";
import { config } from "@/config/environment";
import {
  DASHBOARD_QUERY_OPTIONS,
  DEFAULT_QUERY_OPTIONS
} from "./abstractions/api-config";
import type {
  AdminSearchApplicationsRequest,
  AdminSearchApplicationsResponse,
  AdminPracticesRequest,
  AdminProfessionalsRequest,
  AdminUsersRequest,
  AdminPaymentsRequest,
  AdminDisputesRequest,
  AdminRatingsRequest,
  AdminPaymentItem,
  AdminDisputeItem,
  AdminRatingItem,
  AdminPaginatedResponse,
  SuspendUserRequest,
  ReinstateUserRequest,
  CatalogueEntryKind,
  GetCatalogueResponse,
  SaveCatalogueEntryRequest,
  GetPricingPlansResponse,
  GetPricingPlanHistoryResponse,
  SavePricingPlanRequest,
  GetCurrenciesResponse,
  SaveCurrencyRequest,
  AdminShiftsRequest,
  AdminShiftsResponse,
  TierKind,
  GetTiersResponse,
  SaveTierRequest,
  SaveTierRewardRequest,
  PurgeUserResponse,
  FlagRatingRequest,
  ReviewApplicationRequest,
  ReviewApplicationResponse,
  ReviewDocumentRequest,
  ReviewDocumentResponse
} from "@/domain/admin";
import { DEFAULT_PAGE_SIZE } from "@/constants";
import type {
  ResolveDisputeRequest,
  GetFeesRequest,
  GetFeesResponse,
  WaiveShiftFeeRequest,
  GetSubscriptionInvoicesRequest,
  GetSubscriptionInvoicesResponse,
  WaiveSubscriptionInvoiceRequest
} from "@/domain/payments";
import type { Practice } from "@/domain/practices";
import type { Professional } from "@/domain/professionals";

// Authenticated Admin API hook
export function useAuthenticatedAdminApi() {
  const authenticatedApi = useAuthenticatedApi();
  const { addAuthHeader } = useMsalToken();

  const adminApi = authenticatedApi.injectEndpoints({
    overrideExisting: false,
    // Endpoints
    endpoints: (builder) => ({
      // Get admin dashboard statistics
      getAdminStats: builder.query<AdminStatsResponse, void>({
        query: async () =>
          await buildHttpGet(config, addAuthHeader, API_ENDPOINTS.ADMIN.STATS),
        providesTags: [TAG_BUILDER.ADMIN.REPORTS()],
        ...DASHBOARD_QUERY_OPTIONS,
        keepUnusedDataFor: 5 * 60
      }),

      searchAdminApplications: builder.mutation<
        AdminSearchApplicationsResponse,
        AdminSearchApplicationsRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.APPLICATIONS,
            body
          )
      }),

      /**
       * Application-level review. Body `decision` must be `approved`, `rejected`, or
       * `under_review` (Tempii ReviewApplicationRequestValidator). Per-document review
       * uses `reviewDocument` with `approved` | `rejected` only.
       */
      reviewApplication: builder.mutation<
        ReviewApplicationResponse,
        ReviewApplicationRequest
      >({
        query: async ({ userType, applicationId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.REVIEW_APPLICATION(userType, applicationId),
            body
          ),
        invalidatesTags: (_, __, { userType, applicationId }) => [
          TAG_BUILDER.ADMIN.REPORTS(),
          createTag(API_TAGS.DOCUMENTS, API_TAGS.LIST),
          // Invalidate the specific entity so detail pages refetch
          userType === "practice"
            ? createTag(API_TAGS.PRACTICE, applicationId)
            : createTag(API_TAGS.PROFESSIONAL, applicationId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Review an individual document (body is only status + optional rejectionReason; see Tempii ReviewDocumentRequest)
      reviewDocument: builder.mutation<
        ReviewDocumentResponse,
        ReviewDocumentRequest
      >({
        query: async (request) => {
          const { documentId, status, rejectionReason } = request;
          const body: { status: string; rejectionReason?: string } = { status };
          if (rejectionReason != null && rejectionReason !== "") {
            body.rejectionReason = rejectionReason;
          }
          return await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.REVIEW_DOCUMENT(documentId),
            body
          );
        },
        invalidatesTags: (
          _,
          __,
          { practiceId, professionalId }
        ) => {
          const tags = [
            TAG_BUILDER.ADMIN.REPORTS(),
            createTag(API_TAGS.DOCUMENTS, API_TAGS.LIST)
          ];
          if (practiceId) {
            tags.push(TAG_BUILDER.PRACTICE.ENTITY(practiceId));
            tags.push(...TAG_BUILDER.PRACTICE.DOCUMENTS(practiceId));
          }
          if (professionalId) {
            tags.push(TAG_BUILDER.PROFESSIONAL.ENTITY(professionalId));
            tags.push(...TAG_BUILDER.PROFESSIONAL.DOCUMENTS(professionalId));
          }
          return tags;
        },
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Resolve a dispute
      // Tempii's own receivables: every shift fee in the tenant
      getAdminFees: builder.query<GetFeesResponse, GetFeesRequest>({
        query: async ({ status, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.FEES,
            { status: status || undefined, page, pageSize }
          ),
        providesTags: () => [TAG_BUILDER.PAYMENT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Write a fee off: never charged, reason recorded
      waiveShiftFee: builder.mutation<string, WaiveShiftFeeRequest>({
        query: async ({ feeId, reason }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.WAIVE_FEE(feeId),
            { reason }
          ),
        invalidatesTags: [TAG_BUILDER.PAYMENT.LIST()]
      }),

      // The platform's plan receivables: every subscription invoice in the tenant
      getAdminSubscriptionInvoices: builder.query<
        GetSubscriptionInvoicesResponse,
        GetSubscriptionInvoicesRequest
      >({
        query: async ({ status, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.SUBSCRIPTION_INVOICES,
            { status: status || undefined, page, pageSize }
          ),
        providesTags: () => [TAG_BUILDER.PAYMENT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Write an invoice off: never charged, reason recorded
      waiveSubscriptionInvoice: builder.mutation<string, WaiveSubscriptionInvoiceRequest>({
        query: async ({ invoiceId, reason }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.WAIVE_SUBSCRIPTION_INVOICE(invoiceId),
            { reason }
          ),
        invalidatesTags: [TAG_BUILDER.PAYMENT.LIST()]
      }),

      resolveDispute: builder.mutation<
        string,
        ResolveDisputeRequest & { paymentId: string }
      >({
        query: async ({ paymentId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.RESOLVE_DISPUTE(paymentId),
            body
          ),
        invalidatesTags: [
          TAG_BUILDER.PAYMENT.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get all practices (admin)
      getAdminPractices: builder.mutation<
        AdminPaginatedResponse<Practice>,
        AdminPracticesRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PRACTICES,
            body
          )
      }),

      // Get all professionals (admin)
      getAdminProfessionals: builder.mutation<
        AdminPaginatedResponse<Professional>,
        AdminProfessionalsRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PROFESSIONALS,
            body
          )
      }),

      // Get all users (admin)
      getAdminUsers: builder.mutation<
        AdminPaginatedResponse<User>,
        AdminUsersRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.USERS,
            body
          )
      }),

      // Get a specific user (admin)
      getAdminUser: builder.query<User, string>({
        query: async (userId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.USER(userId)
          ),
        providesTags: (_, __, userId) => [TAG_BUILDER.USER.ENTITY(userId)],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Suspend a user
      suspendUser: builder.mutation<string, { userId: string } & SuspendUserRequest>({
        query: async ({ userId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.SUSPEND_USER(userId),
            body
          ),
        // The list tag and the row tag are separate: invalidating `User:LIST` does not
        // touch `User:{id}`, so without the second entry the user detail page keeps
        // serving the pre-suspension status out of cache.
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.ADMIN.USERS(),
          TAG_BUILDER.USER.ENTITY(userId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // The catalogue is a real query, not a filters-in-the-body mutation like the
      // admin lists, so tag invalidation genuinely refreshes it and the writes below
      // need no explicit refetch.
      getAdminCatalogue: builder.query<
        GetCatalogueResponse,
        { kind?: CatalogueEntryKind } | void
      >({
        query: async (args) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.CATALOGUE,
            args && args.kind ? { kind: args.kind } : undefined
          ),
        providesTags: [TAG_BUILDER.ADMIN.CATALOGUE()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      saveCatalogueEntry: builder.mutation<
        string,
        { kind: CatalogueEntryKind; entryId?: string } & SaveCatalogueEntryRequest
      >({
        // entryId travels as a query parameter (the API takes it [FromQuery]), and
        // buildHttpPost carries only a body — so it is appended here rather than
        // widening the shared helper for one caller.
        query: async ({ kind, entryId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            entryId
              ? `${API_ENDPOINTS.ADMIN.CATALOGUE_SAVE(kind)}?entryId=${encodeURIComponent(entryId)}`
              : API_ENDPOINTS.ADMIN.CATALOGUE_SAVE(kind),
            body
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.CATALOGUE()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      setCatalogueEntryActive: builder.mutation<
        void,
        { kind: CatalogueEntryKind; entryId: string; isActive: boolean }
      >({
        query: async ({ kind, entryId, isActive }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.CATALOGUE_SET_ACTIVE(kind, entryId),
            { isActive }
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.CATALOGUE()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Pricing. Real queries, so tag invalidation refreshes them and the writes below
      // need no explicit refetch — unlike the admin lists, which are mutations.
      getAdminPricingPlans: builder.query<GetPricingPlansResponse, void>({
        query: async () =>
          await buildHttpGet(config, addAuthHeader, API_ENDPOINTS.ADMIN.PRICING_PLANS),
        providesTags: [TAG_BUILDER.ADMIN.PRICING_PLANS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Append-only, so it never needs invalidating by anything but its own plan's writes
      // — which all invalidate the plans tag this shares.
      getPricingPlanHistory: builder.query<GetPricingPlanHistoryResponse, { planId: string }>({
        query: async ({ planId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PRICING_PLAN_HISTORY(planId)
          ),
        providesTags: [TAG_BUILDER.ADMIN.PRICING_PLANS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      savePricingPlan: builder.mutation<
        string,
        { planId?: string } & SavePricingPlanRequest
      >({
        query: async ({ planId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            planId
              ? `${API_ENDPOINTS.ADMIN.PRICING_PLANS}?planId=${encodeURIComponent(planId)}`
              : API_ENDPOINTS.ADMIN.PRICING_PLANS,
            body
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.PRICING_PLANS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      setPricingPlanLive: builder.mutation<void, { planId: string; isLive: boolean }>({
        query: async ({ planId, isLive }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PRICING_PLAN_LIVE(planId),
            { isLive }
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.PRICING_PLANS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      setDefaultPricingPlan: builder.mutation<void, { planId: string }>({
        query: async ({ planId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PRICING_PLAN_DEFAULT(planId),
            {}
          ),
        // The default moves off another plan, so the whole list is stale, not one row.
        invalidatesTags: [TAG_BUILDER.ADMIN.PRICING_PLANS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      getAdminCurrencies: builder.query<GetCurrenciesResponse, void>({
        query: async () =>
          await buildHttpGet(config, addAuthHeader, API_ENDPOINTS.ADMIN.CURRENCIES),
        providesTags: [TAG_BUILDER.ADMIN.CURRENCIES()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      saveCurrency: builder.mutation<
        string,
        { currencyId?: string } & SaveCurrencyRequest
      >({
        query: async ({ currencyId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            currencyId
              ? `${API_ENDPOINTS.ADMIN.CURRENCIES}?currencyId=${encodeURIComponent(currencyId)}`
              : API_ENDPOINTS.ADMIN.CURRENCIES,
            body
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.CURRENCIES()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Loyalty ladders. One tag for both — the two are always read one at a time and a
      // write to either is rare enough that refetching the other costs nothing, whereas
      // a per-kind tag is one more thing to get wrong.
      // A query, not a mutation like the admin lists beside it. Those are mutations
      // because a POST body carries their filters and nothing invalidates them; this
      // console has no writes at all, so a query buys caching, loading state and refetch
      // for free and costs nothing.
      getAdminShifts: builder.query<AdminShiftsResponse, AdminShiftsRequest>({
        query: async (body) =>
          await buildHttpPost(config, addAuthHeader, API_ENDPOINTS.ADMIN.SHIFTS, body),
        providesTags: [TAG_BUILDER.SHIFT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      getAdminTiers: builder.query<GetTiersResponse, { kind: TierKind }>({
        query: async ({ kind }) =>
          await buildHttpGet(config, addAuthHeader, API_ENDPOINTS.ADMIN.TIERS(kind)),
        providesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      saveTier: builder.mutation<
        string,
        { kind: TierKind; tierId?: string } & SaveTierRequest
      >({
        query: async ({ kind, tierId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            tierId
              ? `${API_ENDPOINTS.ADMIN.TIERS(kind)}?tierId=${encodeURIComponent(tierId)}`
              : API_ENDPOINTS.ADMIN.TIERS(kind),
            body
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      setTierActive: builder.mutation<
        void,
        { kind: TierKind; tierId: string; isActive: boolean }
      >({
        query: async ({ kind, tierId, isActive }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.TIER_ACTIVE(kind, tierId),
            { isActive }
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      setDefaultTier: builder.mutation<void, { kind: TierKind; tierId: string }>({
        query: async ({ kind, tierId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.TIER_DEFAULT(kind, tierId),
            {}
          ),
        // The flag moves off another rung, so the whole ladder is stale, not one row.
        invalidatesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      saveTierReward: builder.mutation<
        string,
        { kind: TierKind; tierId: string; rewardId?: string } & SaveTierRewardRequest
      >({
        query: async ({ kind, tierId, rewardId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            rewardId
              ? `${API_ENDPOINTS.ADMIN.TIER_REWARDS(kind, tierId)}?rewardId=${encodeURIComponent(rewardId)}`
              : API_ENDPOINTS.ADMIN.TIER_REWARDS(kind, tierId),
            body
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      deleteTierReward: builder.mutation<
        void,
        { kind: TierKind; tierId: string; rewardId: string }
      >({
        query: async ({ kind, tierId, rewardId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.TIER_REWARD(kind, tierId, rewardId)
          ),
        invalidatesTags: [TAG_BUILDER.ADMIN.TIERS()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // The inverse of suspend, and tagged identically. Note the admin user list is
      // fetched through a mutation, so it is NOT refreshed by this — callers re-run
      // the list explicitly (see UsersManager).
      reinstateUser: builder.mutation<string, { userId: string } & ReinstateUserRequest>({
        query: async ({ userId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.REINSTATE_USER(userId),
            body
          ),
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.ADMIN.USERS(),
          TAG_BUILDER.USER.ENTITY(userId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Permanently purge a user (irreversible). Purging a practice user also
      // removes the practice and all of its member users, so the practices and
      // professionals lists are invalidated alongside the users list.
      purgeUser: builder.mutation<PurgeUserResponse, { userId: string }>({
        query: async ({ userId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PURGE_USER(userId)
          ),
        // The row tag too, or a cached detail page would keep rendering an account
        // that no longer exists.
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.ADMIN.USERS(),
          TAG_BUILDER.USER.ENTITY(userId),
          TAG_BUILDER.PRACTICE.LIST(),
          TAG_BUILDER.PROFESSIONAL.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get all payments (admin)
      getAdminPayments: builder.mutation<
        AdminPaginatedResponse<AdminPaymentItem>,
        AdminPaymentsRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.PAYMENTS,
            body
          )
      }),

      // Get all disputes (admin)
      getAdminDisputes: builder.mutation<
        AdminPaginatedResponse<AdminDisputeItem>,
        AdminDisputesRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.DISPUTES,
            body
          )
      }),

      // Get all ratings (admin)
      getAdminRatings: builder.mutation<
        AdminPaginatedResponse<AdminRatingItem>,
        AdminRatingsRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.RATINGS,
            body
          )
      }),

      // Flag a rating
      flagRating: builder.mutation<
        string,
        { ratingId: string } & FlagRatingRequest
      >({
        query: async ({ ratingId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.FLAG_RATING(ratingId),
            body
          ),
        invalidatesTags: [TAG_BUILDER.REVIEW.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete a rating
      deleteRating: builder.mutation<string, string>({
        query: async (ratingId) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.ADMIN.DELETE_RATING(ratingId)
          ),
        invalidatesTags: [TAG_BUILDER.REVIEW.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      })
    })
  });

  return {
    useGetAdminStatsQuery: adminApi.useGetAdminStatsQuery,
    useSearchAdminApplicationsMutation:
      adminApi.useSearchAdminApplicationsMutation,
    useReviewApplicationMutation: adminApi.useReviewApplicationMutation,
    useReviewDocumentMutation: adminApi.useReviewDocumentMutation,
    useResolveDisputeMutation: adminApi.useResolveDisputeMutation,
    useGetAdminFeesQuery: adminApi.useGetAdminFeesQuery,
    useWaiveShiftFeeMutation: adminApi.useWaiveShiftFeeMutation,
    useGetAdminSubscriptionInvoicesQuery: adminApi.useGetAdminSubscriptionInvoicesQuery,
    useWaiveSubscriptionInvoiceMutation: adminApi.useWaiveSubscriptionInvoiceMutation,
    useGetAdminPracticesMutation: adminApi.useGetAdminPracticesMutation,
    useGetAdminProfessionalsMutation:
      adminApi.useGetAdminProfessionalsMutation,
    useGetAdminUsersMutation: adminApi.useGetAdminUsersMutation,
    useGetAdminUserQuery: adminApi.useGetAdminUserQuery,
    useSuspendUserMutation: adminApi.useSuspendUserMutation,
    useReinstateUserMutation: adminApi.useReinstateUserMutation,
    useGetAdminCatalogueQuery: adminApi.useGetAdminCatalogueQuery,
    useSaveCatalogueEntryMutation: adminApi.useSaveCatalogueEntryMutation,
    useSetCatalogueEntryActiveMutation: adminApi.useSetCatalogueEntryActiveMutation,
    useGetAdminPricingPlansQuery: adminApi.useGetAdminPricingPlansQuery,
    useGetPricingPlanHistoryQuery: adminApi.useGetPricingPlanHistoryQuery,
    useSavePricingPlanMutation: adminApi.useSavePricingPlanMutation,
    useSetPricingPlanLiveMutation: adminApi.useSetPricingPlanLiveMutation,
    useSetDefaultPricingPlanMutation: adminApi.useSetDefaultPricingPlanMutation,
    useGetAdminCurrenciesQuery: adminApi.useGetAdminCurrenciesQuery,
    useSaveCurrencyMutation: adminApi.useSaveCurrencyMutation,
    useGetAdminShiftsQuery: adminApi.useGetAdminShiftsQuery,
    useGetAdminTiersQuery: adminApi.useGetAdminTiersQuery,
    useSaveTierMutation: adminApi.useSaveTierMutation,
    useSetTierActiveMutation: adminApi.useSetTierActiveMutation,
    useSetDefaultTierMutation: adminApi.useSetDefaultTierMutation,
    useSaveTierRewardMutation: adminApi.useSaveTierRewardMutation,
    useDeleteTierRewardMutation: adminApi.useDeleteTierRewardMutation,
    usePurgeUserMutation: adminApi.usePurgeUserMutation,
    useGetAdminPaymentsMutation: adminApi.useGetAdminPaymentsMutation,
    useGetAdminDisputesMutation: adminApi.useGetAdminDisputesMutation,
    useGetAdminRatingsMutation: adminApi.useGetAdminRatingsMutation,
    useFlagRatingMutation: adminApi.useFlagRatingMutation,
    useDeleteRatingMutation: adminApi.useDeleteRatingMutation
  };
}
